import { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Edit2, GripVertical, Save, X, Type, ListOrdered, CheckSquare, Lock, Upload, Check, Asterisk } from 'lucide-react';
import { useProducts } from '../../contexts/ProductContext';
import { useFonts } from '../../contexts/FontContext';
import type { CustomizationField, FieldType, FieldFont } from '@jigflow/shared';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface CustomizationTabProps {
    productId: string;
}

// Font scaling constants and helpers
const FONT_ROW_HEIGHT = 24;
const FONT_BASE_SIZE = 14;

function measureFontTextHeight(text: string, fontFamily: string, fontSize: number): number {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return fontSize * 1.2;
    ctx.font = `${fontSize}px "${fontFamily}"`;
    const metrics = ctx.measureText(text);
    return (metrics.actualBoundingBoxAscent || fontSize * 0.8) +
           (metrics.actualBoundingBoxDescent || fontSize * 0.2);
}

function calculateFontScale(text: string, fontFamily: string, maxHeight: number): number {
    const height = measureFontTextHeight(text, fontFamily, FONT_BASE_SIZE);
    if (height <= maxHeight) return 1;
    return Math.min(1, maxHeight / height);
}

const FIELD_TYPES: { value: FieldType; label: string; icon: typeof Type }[] = [
    { value: 'text', label: 'Text Input', icon: Type },
    { value: 'select', label: 'Dropdown Select', icon: ListOrdered },
    { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
];

// Type-based color coding
const getFieldTypeColors = (type: FieldType, isVariation: boolean = false) => {
    if (isVariation) {
        return {
            border: 'border-l-amber-400',
            bg: 'bg-amber-50/50 dark:bg-amber-900/10',
            icon: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
        };
    }
    switch (type) {
        case 'text':
            return {
                border: 'border-l-blue-400',
                bg: 'hover:bg-blue-50/50 dark:hover:bg-blue-900/10',
                icon: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
            };
        case 'select':
            return {
                border: 'border-l-green-400',
                bg: 'hover:bg-green-50/50 dark:hover:bg-green-900/10',
                icon: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
            };
        case 'checkbox':
            return {
                border: 'border-l-purple-400',
                bg: 'hover:bg-purple-50/50 dark:hover:bg-purple-900/10',
                icon: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
            };
        default:
            return {
                border: 'border-l-slate-300',
                bg: 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                icon: 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
            };
    }
};

// Sortable field item component
interface SortableFieldItemProps {
    field: CustomizationField;
    isVariation: boolean;
    onEdit: () => void;
    onDelete: () => void;
    onToggleRequired: () => void;
    getFieldIcon: (type: FieldType) => typeof Type;
    getDisplayLabel: (field: CustomizationField) => string;
}

function SortableFieldItem({
    field,
    isVariation,
    onEdit,
    onDelete,
    onToggleRequired,
    getFieldIcon,
    getDisplayLabel,
}: SortableFieldItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: field.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const Icon = getFieldIcon(field.type);
    const colors = getFieldTypeColors(field.type, isVariation);

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex items-center gap-3 p-3 border-l-4 ${colors.border} ${colors.bg} transition-all ${
                isDragging ? 'opacity-50 z-50 shadow-lg bg-white dark:bg-slate-800' : ''
            }`}
        >
            {/* Drag Handle */}
            <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1"
            >
                <GripVertical size={18} />
            </div>

            {/* Field Icon */}
            <div className={`p-2 rounded-lg ${colors.icon}`}>
                {isVariation ? <Lock size={16} /> : <Icon size={16} />}
            </div>

            {/* Field Info - Compact Layout */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800 dark:text-white truncate">
                        {getDisplayLabel(field)}
                    </span>
                    {field.required && (
                        <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-medium rounded">
                            Required
                        </span>
                    )}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mt-0.5">
                    <span className="capitalize font-medium">{field.type}</span>
                    {field.type === 'text' && field.maxLength && (
                        <>
                            <span className="text-slate-300 dark:text-slate-600">|</span>
                            <span>Max {field.maxLength}</span>
                        </>
                    )}
                    {field.type === 'select' && field.options && (
                        <>
                            <span className="text-slate-300 dark:text-slate-600">|</span>
                            <span>{field.options.length} options</span>
                        </>
                    )}
                    {isVariation && (
                        <>
                            <span className="text-slate-300 dark:text-slate-600">|</span>
                            <span className="text-amber-600 dark:text-amber-400">Auto-generated</span>
                        </>
                    )}
                </div>
            </div>

            {/* Actions */}
            {isVariation ? (
                <button
                    onClick={onToggleRequired}
                    className={`p-1.5 rounded-lg transition ${
                        field.required
                            ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30'
                            : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                    title={field.required ? 'Required - click to make optional' : 'Optional - click to make required'}
                >
                    <Asterisk size={16} />
                </button>
            ) : (
                <div className="flex items-center gap-0.5">
                    <button
                        onClick={onEdit}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition"
                        title="Edit field"
                    >
                        <Edit2 size={16} />
                    </button>
                    <button
                        onClick={onDelete}
                        className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition"
                        title="Delete field"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            )}
        </div>
    );
}

export default function CustomizationTab({ productId }: CustomizationTabProps) {
    const {
        getFieldsByProductId,
        addField,
        updateField,
        deleteField,
        reorderFields
    } = useProducts();
    const {
        fonts,
        addFont,
        getFieldFonts,
        addFieldFont,
        removeFieldFont,
        setFieldPrimaryFont
    } = useFonts();

    const fontInputRef = useRef<HTMLInputElement>(null);

    const [fields, setFields] = useState<CustomizationField[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingField, setEditingField] = useState<CustomizationField | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [fieldFontsState, setFieldFontsState] = useState<FieldFont[]>([]);
    const [showFontPicker, setShowFontPicker] = useState(false);
    const [pendingFontIds, setPendingFontIds] = useState<string[]>([]);

    // @dnd-kit sensors for drag-to-reorder
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    // Load fields on mount
    useEffect(() => {
        loadFields();
    }, [productId]);

    const loadFields = async () => {
        setLoading(true);
        const loadedFields = await getFieldsByProductId(productId);
        setFields(loadedFields);
        setLoading(false);
    };

    // Load fonts for a specific field when editing
    const loadFieldFontsForEdit = async (fieldId: string) => {
        if (!fieldId) {
            setFieldFontsState([]);
            return;
        }
        const loadedFonts = await getFieldFonts(fieldId);
        setFieldFontsState(loadedFonts);
    };

    const handleRemoveFontFromField = async (fontId: string) => {
        if (!editingField?.id) return;

        try {
            await removeFieldFont(editingField.id, fontId);
            const updatedFonts = fieldFontsState.filter(f => f.id !== fontId);

            if (updatedFonts.length > 0 && !updatedFonts.some(f => f.isPrimary)) {
                updatedFonts[0].isPrimary = true;
            }
            setFieldFontsState(updatedFonts);

            const primaryFont = updatedFonts.find(f => f.isPrimary);
            if (editingField.fontFamily === fonts.find(f => f.id === fontId)?.family && primaryFont) {
                setEditingField({ ...editingField, fontFamily: primaryFont.family } as CustomizationField);
            }
            console.log('[CustomizationTab] Font removed');
        } catch (e) {
            console.error('[CustomizationTab] Failed to remove font:', e);
        }
    };

    const handleSetPrimaryFont = async (fontId: string) => {
        if (!editingField?.id) return;

        try {
            await setFieldPrimaryFont(editingField.id, fontId);
            setFieldFontsState(prev => prev.map(f => ({
                ...f,
                isPrimary: f.id === fontId
            })).sort((a, b) => {
                if (a.isPrimary && !b.isPrimary) return -1;
                if (!a.isPrimary && b.isPrimary) return 1;
                return a.name.localeCompare(b.name);
            }));
            console.log('[CustomizationTab] Default font updated');
        } catch (e) {
            console.error('[CustomizationTab] Failed to set default font:', e);
        }
    };

    const handleAddFontToField = async (fontId: string) => {
        if (!fontId) return;

        const font = fonts.find(f => f.id === fontId);
        if (!font) return;

        // During field creation: add to pending list
        if (isCreating) {
            if (!pendingFontIds.includes(fontId)) {
                setPendingFontIds(prev => [...prev, fontId]);
            }
            return;
        }

        // Editing existing field: save to database
        if (!editingField?.id) return;

        try {
            const isPrimary = fieldFontsState.length === 0;
            await addFieldFont(editingField.id, fontId, isPrimary);

            const newFieldFont: FieldFont = {
                ...font,
                isPrimary
            };
            setFieldFontsState(prev => {
                const updated = [...prev, newFieldFont];
                return updated.sort((a, b) => {
                    if (a.isPrimary && !b.isPrimary) return -1;
                    if (!a.isPrimary && b.isPrimary) return 1;
                    return a.name.localeCompare(b.name);
                });
            });
            console.log('[CustomizationTab] Font added');
        } catch (e) {
            console.error('[CustomizationTab] Failed to add font:', e);
        }
    };

    const handleCreateField = () => {
        setIsCreating(true);
        setFieldFontsState([]);
        setPendingFontIds([]);
        setShowFontPicker(false);
        setEditingField({
            id: '',
            productId,
            label: '',
            type: 'text',
            required: false,
            options: [],
            maxLength: 50,
            orderIndex: fields.length,
            score: false,
            engrave: true,
            fontFamily: 'Roboto'
        });
    };

    // Handle font file upload - uploads to system AND adds to this field
    const handleFontUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.name.match(/\.(ttf|otf|woff|woff2)$/i)) {
            console.error('[CustomizationTab] Invalid font file type');
            return;
        }

        try {
            // SaaS addFont takes (file, name, family) - extract from filename
            const baseName = file.name.replace(/\.(ttf|otf|woff|woff2)$/i, '');
            const newFont = await addFont(file, baseName, baseName);

            if (newFont && editingField?.id) {
                const isPrimary = fieldFontsState.length === 0;
                await addFieldFont(editingField.id, newFont.id, isPrimary);

                const newFieldFont: FieldFont = {
                    ...newFont,
                    isPrimary
                };
                setFieldFontsState(prev => {
                    const updated = [...prev, newFieldFont];
                    return updated.sort((a, b) => {
                        if (a.isPrimary && !b.isPrimary) return -1;
                        if (!a.isPrimary && b.isPrimary) return 1;
                        return a.name.localeCompare(b.name);
                    });
                });

                setEditingField({ ...editingField, fontFamily: newFont.family });
            }
        } catch (error) {
            console.error('[CustomizationTab] Failed to upload font:', error);
        }

        if (fontInputRef.current) {
            fontInputRef.current.value = '';
        }
    };

    const handleSaveField = async () => {
        if (!editingField) return;

        if (!editingField.label.trim()) {
            console.error('[CustomizationTab] Field label is required');
            return;
        }

        if (editingField.type === 'select' && (!editingField.options || editingField.options.length === 0)) {
            console.error('[CustomizationTab] Select fields must have at least one option');
            return;
        }

        try {
            if (isCreating) {
                const newField = await addField({
                    productId: editingField.productId,
                    label: editingField.label,
                    type: editingField.type,
                    required: editingField.required,
                    options: editingField.options,
                    maxLength: editingField.maxLength,
                    orderIndex: editingField.orderIndex,
                    score: editingField.score,
                    engrave: editingField.engrave,
                    fontFamily: editingField.fontFamily
                });
                setFields([...fields, newField]);

                // Assign fonts for text fields
                if (newField.type === 'text') {
                    if (pendingFontIds.length === 0) {
                        const robotoFont = fonts.find(f => f.id === '__roboto__' || f.family === 'Roboto');
                        if (robotoFont) {
                            await addFieldFont(newField.id, robotoFont.id, true);
                        }
                    } else {
                        for (let i = 0; i < pendingFontIds.length; i++) {
                            await addFieldFont(newField.id, pendingFontIds[i], i === 0);
                        }
                    }
                }

                setPendingFontIds([]);
                setShowFontPicker(false);
                console.log('[CustomizationTab] Field created');
            } else {
                await updateField(editingField);
                setFields(fields.map(f => f.id === editingField.id ? editingField : f));
                console.log('[CustomizationTab] Field updated');
            }
            setEditingField(null);
            setIsCreating(false);
        } catch (error) {
            console.error('[CustomizationTab] Failed to save field:', error);
        }
    };

    const handleDeleteField = async (field: CustomizationField) => {
        if (field.label === '__variation_selector__') {
            console.error('[CustomizationTab] Cannot delete variation selector');
            return;
        }

        if (!confirm(`Delete field "${field.label}"?`)) return;

        try {
            await deleteField(field.id);
            setFields(fields.filter(f => f.id !== field.id));
            console.log('[CustomizationTab] Field deleted');
        } catch (error) {
            console.error('[CustomizationTab] Failed to delete field:', error);
        }
    };

    // @dnd-kit drag end handler
    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = fields.findIndex(f => f.id === active.id);
        const newIndex = fields.findIndex(f => f.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            const reorderedFields = arrayMove(fields, oldIndex, newIndex).map((f, idx) => ({
                ...f,
                orderIndex: idx
            }));
            setFields(reorderedFields);

            try {
                await reorderFields(productId, reorderedFields.map(f => f.id));
            } catch (error) {
                console.error('[CustomizationTab] Failed to reorder fields:', error);
                loadFields();
            }
        }
    };

    // Options management for select fields
    const handleAddOption = () => {
        if (!editingField) return;
        const newOption = `Option ${(editingField.options?.length || 0) + 1}`;
        setEditingField({
            ...editingField,
            options: [...(editingField.options || []), newOption]
        });
    };

    const handleUpdateOption = (index: number, value: string) => {
        if (!editingField) return;
        const newOptions = [...(editingField.options || [])];
        newOptions[index] = value;
        setEditingField({ ...editingField, options: newOptions });
    };

    const handleRemoveOption = (index: number) => {
        if (!editingField) return;
        const newOptions = (editingField.options || []).filter((_, i) => i !== index);
        setEditingField({ ...editingField, options: newOptions });
    };

    const isVariationField = (field: CustomizationField) => {
        return field.label === '__variation_selector__';
    };

    const handleToggleVariationRequired = async (field: CustomizationField) => {
        try {
            const updatedField = { ...field, required: !field.required };
            await updateField(updatedField);
            setFields(fields.map(f => f.id === field.id ? updatedField : f));
        } catch (error) {
            console.error('[CustomizationTab] Failed to update variation:', error);
        }
    };

    const getFieldDisplayLabel = (field: CustomizationField) => {
        if (isVariationField(field)) return 'Variation Selector';
        return field.label;
    };

    const getFieldIcon = (type: FieldType) => {
        const found = FIELD_TYPES.find(t => t.value === type);
        return found?.icon || Type;
    };

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="text-slate-400">Loading fields...</div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto p-6">
            <div className="max-w-2xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-bold text-slate-800 dark:text-white">Customization Fields</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            Define the fields customers will fill out to personalize this product.
                        </p>
                    </div>
                    <button
                        onClick={handleCreateField}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition"
                    >
                        <Plus size={18} />
                        Add Field
                    </button>
                </div>

                {/* Fields List */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                    {fields.length === 0 ? (
                        <div className="p-8 text-center text-slate-400">
                            <Type size={48} className="mx-auto mb-3 opacity-50" />
                            <p>No customization fields yet.</p>
                            <p className="text-sm">Add fields to let customers personalize this product.</p>
                        </div>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={fields.map(f => f.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="divide-y divide-slate-100 dark:divide-slate-700">
                                    {fields.map((field) => (
                                        <SortableFieldItem
                                            key={field.id}
                                            field={field}
                                            isVariation={isVariationField(field)}
                                            onEdit={() => {
                                                setEditingField(field);
                                                setIsCreating(false);
                                                loadFieldFontsForEdit(field.id);
                                            }}
                                            onDelete={() => handleDeleteField(field)}
                                            onToggleRequired={() => handleToggleVariationRequired(field)}
                                            getFieldIcon={getFieldIcon}
                                            getDisplayLabel={getFieldDisplayLabel}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    )}
                </div>

                {/* Hint */}
                <p className="text-sm text-slate-400 text-center">
                    Drag fields to reorder how they appear to customers.
                </p>
            </div>

            {/* Edit/Create Modal */}
            {editingField && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
                            <h3 className="font-bold text-lg text-slate-800 dark:text-white">
                                {isCreating ? 'New Field' : 'Edit Field'}
                            </h3>
                            <button
                                onClick={() => { setEditingField(null); setIsCreating(false); setPendingFontIds([]); setShowFontPicker(false); }}
                                className="text-slate-400 hover:text-slate-600"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
                            {/* Compact top section */}
                            <div className="flex gap-2 items-center">
                                <input
                                    type="text"
                                    value={editingField.label}
                                    onChange={e => setEditingField({ ...editingField, label: e.target.value })}
                                    placeholder="Field label"
                                    className="flex-1 px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700 dark:text-white"
                                />
                                {editingField.type === 'text' && (
                                    <input
                                        type="number"
                                        min="1"
                                        max="500"
                                        value={editingField.maxLength || 50}
                                        onChange={e => setEditingField({ ...editingField, maxLength: parseInt(e.target.value) || 50 })}
                                        className="w-16 px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700 dark:text-white text-center"
                                        title="Max length"
                                    />
                                )}
                                <label className="flex items-center gap-1.5 cursor-pointer text-sm text-slate-600 dark:text-slate-400">
                                    <input
                                        type="checkbox"
                                        checked={editingField.required}
                                        onChange={e => setEditingField({ ...editingField, required: e.target.checked })}
                                        className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    Required
                                </label>
                            </div>

                            {/* Compact field type selector */}
                            <div className="flex gap-1">
                                {FIELD_TYPES.map(type => {
                                    const Icon = type.icon;
                                    const isSelected = editingField.type === type.value;
                                    return (
                                        <button
                                            key={type.value}
                                            onClick={() => setEditingField({ ...editingField, type: type.value })}
                                            className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg border transition text-sm ${
                                                isSelected
                                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600'
                                                    : 'border-slate-200 dark:border-slate-600 text-slate-500 hover:border-slate-300 dark:hover:border-slate-500'
                                            }`}
                                        >
                                            <Icon size={14} />
                                            <span className="text-xs">{type.label}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Select-specific: Options */}
                            {editingField.type === 'select' && (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Options *
                                        </label>
                                        <button
                                            onClick={handleAddOption}
                                            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                        >
                                            + Add Option
                                        </button>
                                    </div>
                                    <div className="space-y-2 max-h-40 overflow-y-auto">
                                        {(editingField.options || []).map((option, idx) => (
                                            <div key={idx} className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={option}
                                                    onChange={e => handleUpdateOption(idx, e.target.value)}
                                                    placeholder={`Option ${idx + 1}`}
                                                    className="flex-1 p-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700 dark:text-white text-sm"
                                                />
                                                <button
                                                    onClick={() => handleRemoveOption(idx)}
                                                    className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ))}
                                        {(!editingField.options || editingField.options.length === 0) && (
                                            <p className="text-sm text-slate-400 italic">No options yet. Add at least one.</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Font Selection - Text fields only */}
                            {editingField.type === 'text' && (
                                <div className="border border-slate-200 dark:border-slate-600 rounded-lg overflow-hidden">
                                    {/* Header row */}
                                    <div className="flex items-center justify-between px-2 py-1.5 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-600">
                                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                                            Fonts
                                        </span>
                                        <div className="flex items-center gap-2">
                                            {!isCreating && (
                                                <label className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 cursor-pointer" title="Upload new font">
                                                    <Upload size={10} />
                                                    <span>Upload</span>
                                                    <input
                                                        ref={fontInputRef}
                                                        type="file"
                                                        accept=".ttf,.otf,.woff,.woff2"
                                                        className="hidden"
                                                        onChange={handleFontUpload}
                                                    />
                                                </label>
                                            )}
                                            <button
                                                onClick={() => setShowFontPicker(!showFontPicker)}
                                                className="flex items-center gap-0.5 text-xs text-blue-600 hover:text-blue-700"
                                            >
                                                <Plus size={10} />
                                                <span>Add</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Font rows */}
                                    <div className="divide-y divide-slate-100 dark:divide-slate-700 max-h-32 overflow-y-auto">
                                        {/* Pending fonts during creation */}
                                        {isCreating && pendingFontIds.map((fontId, idx) => {
                                            const font = fonts.find(f => f.id === fontId);
                                            if (!font) return null;
                                            const isDefault = idx === 0;
                                            const scale = calculateFontScale(font.name, font.family, FONT_ROW_HEIGHT - 4);
                                            return (
                                                <div key={fontId} className={`flex items-center justify-between px-2 ${isDefault ? 'bg-green-50/50 dark:bg-green-900/10' : 'hover:bg-slate-50 dark:hover:bg-slate-700/30'}`} style={{ height: `${FONT_ROW_HEIGHT}px` }}>
                                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                                        <div className="flex-1 min-w-0 overflow-hidden">
                                                            <span
                                                                className="text-slate-800 dark:text-white whitespace-nowrap"
                                                                style={{
                                                                    fontFamily: font.family,
                                                                    fontSize: `${FONT_BASE_SIZE}px`,
                                                                    transform: `scale(${scale})`,
                                                                    transformOrigin: 'left center',
                                                                    display: 'inline-block'
                                                                }}
                                                            >
                                                                {font.name}
                                                            </span>
                                                        </div>
                                                        <span className="text-[10px] text-slate-400 shrink-0 w-12 text-center">
                                                            {font.style || 'Regular'}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-0.5 shrink-0">
                                                        <button
                                                            onClick={() => setPendingFontIds(prev => [fontId, ...prev.filter(id => id !== fontId)])}
                                                            className={`p-0.5 rounded transition ${isDefault ? 'text-green-600' : 'text-slate-400 hover:text-green-600'}`}
                                                            title={isDefault ? 'Default font' : 'Set as default'}
                                                        >
                                                            <Check size={12} />
                                                        </button>
                                                        <button
                                                            onClick={() => setPendingFontIds(prev => prev.filter(id => id !== fontId))}
                                                            className="p-0.5 text-slate-400 hover:text-red-500 rounded transition"
                                                            title="Remove"
                                                        >
                                                            <X size={12} />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* Existing field fonts when editing */}
                                        {!isCreating && fieldFontsState.map(font => {
                                            const scale = calculateFontScale(font.name, font.family, FONT_ROW_HEIGHT - 4);
                                            return (
                                                <div key={font.id} className={`flex items-center justify-between px-2 ${font.isPrimary ? 'bg-green-50/50 dark:bg-green-900/10' : 'hover:bg-slate-50 dark:hover:bg-slate-700/30'}`} style={{ height: `${FONT_ROW_HEIGHT}px` }}>
                                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                                        <div className="flex-1 min-w-0 overflow-hidden">
                                                            <span
                                                                className="text-slate-800 dark:text-white whitespace-nowrap"
                                                                style={{
                                                                    fontFamily: font.family,
                                                                    fontSize: `${FONT_BASE_SIZE}px`,
                                                                    transform: `scale(${scale})`,
                                                                    transformOrigin: 'left center',
                                                                    display: 'inline-block'
                                                                }}
                                                            >
                                                                {font.name}
                                                            </span>
                                                        </div>
                                                        <span className="text-[10px] text-slate-400 shrink-0 w-12 text-center">
                                                            {font.style || 'Regular'}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-0.5 shrink-0">
                                                        <button
                                                            onClick={() => handleSetPrimaryFont(font.id)}
                                                            className={`p-0.5 rounded transition ${font.isPrimary ? 'text-green-600' : 'text-slate-400 hover:text-green-600'}`}
                                                            title={font.isPrimary ? 'Default font' : 'Set as default'}
                                                        >
                                                            <Check size={12} />
                                                        </button>
                                                        {fieldFontsState.length > 1 && (
                                                            <button
                                                                onClick={() => handleRemoveFontFromField(font.id)}
                                                                className="p-0.5 text-slate-400 hover:text-red-500 rounded transition"
                                                                title="Remove"
                                                            >
                                                                <X size={12} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* Empty state */}
                                        {isCreating && pendingFontIds.length === 0 && (
                                            <div className="px-2 py-2 text-xs text-slate-400 text-center">
                                                Roboto will be used if no fonts selected
                                            </div>
                                        )}
                                        {!isCreating && fieldFontsState.length === 0 && (
                                            <div className="px-2 py-2 text-xs text-amber-600 dark:text-amber-400 text-center">
                                                No fonts assigned yet
                                            </div>
                                        )}
                                    </div>

                                    {/* Font picker dropdown */}
                                    {showFontPicker && (
                                        <>
                                            <div
                                                className="fixed inset-0 z-[70]"
                                                onClick={() => setShowFontPicker(false)}
                                            />
                                            <div className="absolute left-4 right-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-xl z-[80] overflow-hidden">
                                                <div className="max-h-40 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700">
                                                    {fonts.filter(f => !fieldFontsState.some(ff => ff.id === f.id) && !pendingFontIds.includes(f.id)).length === 0 ? (
                                                        <div className="px-2 py-2 text-xs text-slate-500 text-center">All fonts already added</div>
                                                    ) : (
                                                        fonts.filter(f => !fieldFontsState.some(ff => ff.id === f.id) && !pendingFontIds.includes(f.id)).map(font => {
                                                            const scale = calculateFontScale(font.name, font.family, FONT_ROW_HEIGHT - 4);
                                                            return (
                                                                <button
                                                                    key={font.id}
                                                                    onClick={() => {
                                                                        handleAddFontToField(font.id);
                                                                        setShowFontPicker(false);
                                                                    }}
                                                                    className="w-full px-2 text-left hover:bg-blue-50 dark:hover:bg-blue-900/30 transition flex items-center justify-between gap-2"
                                                                    style={{ height: `${FONT_ROW_HEIGHT}px` }}
                                                                >
                                                                    <div className="flex-1 min-w-0 overflow-hidden">
                                                                        <span
                                                                            className="text-slate-800 dark:text-white whitespace-nowrap"
                                                                            style={{
                                                                                fontFamily: font.family,
                                                                                fontSize: `${FONT_BASE_SIZE}px`,
                                                                                transform: `scale(${scale})`,
                                                                                transformOrigin: 'left center',
                                                                                display: 'inline-block'
                                                                            }}
                                                                        >
                                                                            {font.name}
                                                                        </span>
                                                                    </div>
                                                                    <span className="text-[10px] text-slate-400 shrink-0 w-12 text-center">
                                                                        {font.style || 'Regular'}
                                                                    </span>
                                                                    {font.isSystem && <span className="text-[10px] text-slate-400 shrink-0">(system)</span>}
                                                                </button>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="p-4 bg-slate-50 dark:bg-slate-700/50 flex justify-end gap-2">
                            <button
                                onClick={() => { setEditingField(null); setIsCreating(false); setPendingFontIds([]); setShowFontPicker(false); }}
                                className="px-4 py-2 text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-600 rounded-lg transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveField}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                            >
                                <Save size={18} />
                                {isCreating ? 'Create Field' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
